import React, { useState } from 'react';
import { motion } from 'framer-motion';

const MotionDiv = motion.div;

const CompanyUpdates = () => {
  // Sample company updates data
  const [updates] = useState([
    {
      id: 1,
      type: 'announcement',
      title: 'Phase 1 Beta Testing Now Live!',
      date: 'December 15, 2024',
      category: 'Beta Release',
      excerpt: 'We\'re excited to announce that POSlah Phase 1 Beta is now available for selected testers. Join us in revolutionizing the POS experience!',
      content: 'Our Phase 1 Beta includes core POS functionality, inventory management, and basic reporting features. We\'re looking for feedback from hawkers and small business owners.',
      badge: 'Live Now',
      badgeColor: 'success',
      priority: 'high'
    },
    {
      id: 2,
      type: 'update',
      title: 'New Beta Features Released',
      date: 'December 10, 2024',
      category: 'Feature Update',
      excerpt: 'Enhanced payment processing, improved inventory tracking, and new dashboard analytics now available for beta testers.',
      content: 'Latest beta update includes multi-payment support, real-time inventory sync, and advanced sales analytics dashboard.',
      badge: 'New Features',
      badgeColor: 'info',
      priority: 'medium'
    },
    {
      id: 3,
      type: 'roadmap',
      title: 'Phase 2: AI-Powered Analytics Coming Soon',
      date: 'December 5, 2024',
      category: 'Roadmap',
      excerpt: 'Phase 2 will introduce AI-powered sales predictions, customer behavior analysis, and automated inventory suggestions.',
      content: 'Our AI engine will help businesses make data-driven decisions by analyzing sales patterns, predicting demand, and optimizing inventory levels.',
      badge: 'Coming Soon',
      badgeColor: 'warning',
      priority: 'medium'
    },
    {
      id: 4,
      type: 'announcement',
      title: 'Partnership with Singapore Food Courts',
      date: 'November 28, 2024',
      category: 'Partnership',
      excerpt: 'POSlah partners with major Singapore food courts to pilot our integrated POS solution across multiple hawker stalls.',
      content: 'This partnership will allow us to test our system at scale and gather valuable feedback from diverse food service operations.',
      badge: 'Partnership',
      badgeColor: 'primary',
      priority: 'high'
    },
    {
      id: 5,
      type: 'update',
      title: 'Mobile App Beta Testing',
      date: 'November 20, 2024',
      category: 'Mobile Update',
      excerpt: 'POSlah mobile companion app now available for beta testers. Manage your business on the go!',
      content: 'The mobile app allows business owners to monitor sales, update inventory, and view analytics from anywhere.',
      badge: 'Mobile Beta',
      badgeColor: 'info',
      priority: 'medium'
    },
    {
      id: 6,
      type: 'community',
      title: 'Beta Tester Spotlight: Ah Beng\'s Char Kway Teow',
      date: 'November 15, 2024',
      category: 'Success Story',
      excerpt: 'See how POSlah helped increase efficiency by 40% at this popular hawker stall in Chinatown.',
      content: 'Ah Beng shares his experience using POSlah during our beta testing phase and the improvements he\'s seen in his daily operations.',
      badge: 'Success Story',
      badgeColor: 'success',
      priority: 'low'
    }
  ]);

  const [selectedUpdate, setSelectedUpdate] = useState(null);

  const getBadgeClass = (color) => {
    return `update-badge ${color}`;
  };

  const getPriorityClass = (priority) => {
    return `update-card ${priority}-priority`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const staggerContainer = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="company-updates">
      {/* Header */}
      <MotionDiv 
        className="updates-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="section-title">Company Updates</h2>
        <p className="section-description">
          Stay up to date with POSlah's latest developments, beta releases, and company milestones
        </p>
      </MotionDiv>

      {/* Featured Update */}
      {updates.length > 0 && (
        <MotionDiv 
          className="featured-update"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="featured-content">
            <div className={getBadgeClass(updates[0].badgeColor)}>
              {updates[0].badge}
            </div>
            <h3 className="featured-title">{updates[0].title}</h3>
            <p className="featured-date">{formatDate(updates[0].date)}</p>
            <p className="featured-excerpt">{updates[0].excerpt}</p>
            <button 
              className="read-more-btn"
              onClick={() => setSelectedUpdate(updates[0])}
            >
              Read Full Update
              <span className="btn-arrow">→</span>
            </button>
          </div>
          <div className="featured-visual">
            <div className="pulse-circle">
              <div className="inner-circle">
                <span className="featured-icon">🚀</span>
              </div>
            </div>
          </div>
        </MotionDiv>
      )}

      {/* Updates Grid */}
      <MotionDiv 
        className="updates-grid"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        {updates.slice(1).map((update) => (
          <MotionDiv
            key={update.id}
            className={getPriorityClass(update.priority)}
            variants={cardVariants}
            whileHover={{ 
              y: -5, 
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)" 
            }}
            onClick={() => setSelectedUpdate(update)}
          >
            <div className="update-header">
              <div className={getBadgeClass(update.badgeColor)}>
                {update.badge}
              </div>
              <span className="update-category">{update.category}</span>
            </div>
            
            <h4 className="update-title">{update.title}</h4>
            <p className="update-date">{formatDate(update.date)}</p>
            <p className="update-excerpt">{update.excerpt}</p>
            
            <div className="update-footer">
              <button className="read-more-link">
                Read More
                <span className="arrow">→</span>
              </button>
            </div>
          </MotionDiv>
        ))}
      </MotionDiv>

      {/* Update Detail Modal */}
      {selectedUpdate && (
        <MotionDiv 
          className="update-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setSelectedUpdate(null)}
        >
          <MotionDiv 
            className="update-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close"
              onClick={() => setSelectedUpdate(null)}
            >
              ✕
            </button>
            
            <div className="modal-header">
              <div className={getBadgeClass(selectedUpdate.badgeColor)}>
                {selectedUpdate.badge}
              </div>
              <h3 className="modal-title">{selectedUpdate.title}</h3>
              <p className="modal-date">{formatDate(selectedUpdate.date)}</p>
              <span className="modal-category">{selectedUpdate.category}</span>
            </div>
            
            <div className="modal-content">
              <p className="modal-excerpt">{selectedUpdate.excerpt}</p>
              <div className="modal-divider"></div>
              <div className="modal-full-content">
                <p>{selectedUpdate.content}</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="close-modal-btn"
                onClick={() => setSelectedUpdate(null)}
              >
                Close
              </button>
            </div>
          </MotionDiv>
        </MotionDiv>
      )}
    </div>
  );
};

export default CompanyUpdates;